import React, { useEffect, useState, useRef } from 'react';
import moment from 'moment-timezone';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl } from '@material-ui/core';
import LocalizedDatePicker from '../../../../custom-widgets/LocalizedDatePicker';
import { DateTimePicker, DatePicker, TimePicker } from '@mui/x-date-pickers-pro';

const pickerMap = {
    DateTimePicker,
    DatePicker,
    TimePicker,
};

const DateTimeSelector = ({ name, props, Picker, referenceFieldMin, referenceFieldMax, reference, isEdit, disableIfDefault, autoAdjust }) => {
    const {
        control,
        formState: { errors, defaultValues },
        getValues,
        setValue,
        watch,
        selectedValues,
        trigger,
        // wrap
    } = useFormContext();

    const error = errors[name];
    const { referenceField, referenceKey, timeOnly } = reference || {};
    const { referenceField: autoAdjustReferenceField, adjustment, unit, alwaysAdjust } = autoAdjust || {};
    const autoAdjustReferenceValue = watch(autoAdjustReferenceField);
    const hasAdjusted = useRef(false);

    const minDate = referenceFieldMin && getValues(referenceFieldMin) ? moment(getValues(referenceFieldMin)) : undefined;
    const maxDate = referenceFieldMax && getValues(referenceFieldMax) ? moment(getValues(referenceFieldMax)) : undefined;
    const [initialReference, setInitialReference] = useState();

    const momentize = (value, format) => (moment.isMoment(value) ? value : moment(value, format));

    useEffect(() => {
        const referenceValue = selectedValues[referenceField];

        if (!referenceValue || (disableIfDefault && defaultValues[name])) return;

        let value = getValues(name);
        let newValue = referenceValue[referenceKey];
        newValue = momentize(newValue, 'HH:mm');

        if (timeOnly) {
            value = momentize(value).format('YYYY-MM-DD');
            newValue = moment(`${value}-${newValue.format('HH:mm')}`, 'YYYY-MM-DD-HH:mm');
        }

        const referenceHasChanged = initialReference && initialReference?._id !== referenceValue?._id;
        if (!referenceHasChanged) {
            setInitialReference(referenceValue);
        }

        // ensures value is not overwritten on edit unless the reference value changes
        if (referenceHasChanged || !isEdit) {
            setValue(name, newValue);
        }
    }, [watch(referenceField ?? '')]);

    useEffect(() => {
        if (!autoAdjust) return;
        const value = getValues(name);

        // if the value is not between the min and max date, auto adjust the value
        const isErrant = !value || moment(value).isAfter(maxDate) || moment(value).isBefore(minDate);
        if (isErrant && (!hasAdjusted.current || alwaysAdjust)) {
            hasAdjusted.current = true;
            setValue(name, moment(autoAdjustReferenceValue).clone().add(adjustment, unit));
            trigger(name);
        }
    }, [autoAdjustReferenceValue]);

    return (
        <FormControl fullWidth style={{ margin: '8px 0' }}>
            <Controller
                control={control}
                name={name}
                render={({ field: { onChange, value } }) => {
                    let momentValue = value ?? null;
                    if (!!value && !moment.isMoment(value)) momentValue = moment(value);
                    return (
                        <LocalizedDatePicker
                            Picker={pickerMap[Picker] ?? DateTimePicker}
                            onChange={(val) => onChange(val)}
                            value={momentValue}
                            error={!!error}
                            helperText={error?.message}
                            minDateTime={minDate}
                            maxDateTime={maxDate}
                            InputLabelProps={{ shrink: true }}
                            {...props}
                            // wrap
                        />
                    );
                }}
            />
        </FormControl>
    );
};

export default DateTimeSelector;
